import { useMemo } from 'react';
import { applyVueInReact } from 'vuereact-combined';

import antConfig from '@/ant-config';
import BillingDowngradePendingDataProvider from '@/components/Billing/BillingDowngradePendingDataProvider.vue';
import { SECTION_DESCRIPTION } from '@/components/Billing/billing-service.const';
import RouterLink from '@/react_app/common/RouterLink';
import { AppButton, AppProgress } from '@/react_app/components/Atoms/index';
import SectionCard from '@/react_app/components/BillingUsage/Global/SectionCard';
import { FeaturesSectionProps } from '@/react_app/components/BillingUsage/Overview/types';
import { addCommasToNumber } from '@/utils/helpers';

const DowngradeIndication = applyVueInReact(BillingDowngradePendingDataProvider);
const FeaturesCard = ({
	noun,
	videoUrl,
	onUpgrade,
	seeUsageTo,
	totalTasks,
	productType,
	sectionTitle,
	quotaEndDate,
	quotaStartDate,
	planReadableName,
	currentUsedTasks,
	showPendingDowngrade,
	isOveragesEnabled,
	overageAmount,
	estimatedOveragesCost,
	isInTrial,
	endOfTrial,
}: FeaturesSectionProps) => {
	const usagePercentage = useMemo(() => {
		const res = Math.floor((currentUsedTasks / totalTasks) * 100);
		if (isNaN(res)) {
			return 100;
		} else {
			return res;
		}
	}, [currentUsedTasks, totalTasks]);
	const isUnlimitedTasks = useMemo(() => !(totalTasks > 0), [totalTasks]);
	const shouldShowTrial = useMemo(
		() => isInTrial && endOfTrial && planReadableName.toLowerCase().includes('free'),
		[isInTrial, endOfTrial, planReadableName]
	);
	return (
		<SectionCard
			className="max-w-6xl"
			sectionTitle={sectionTitle}
			sectionSubtitle={
				<>
					{SECTION_DESCRIPTION[productType]}
					{'. '}
					{videoUrl && (
						<RouterLink
							to={{ path: videoUrl }}
							target="_blank"
							className="text-xs underline hover:no-underline"
							style={{ color: antConfig.token?.colorText }}
						>
							Watch video
						</RouterLink>
					)}
				</>
			}
			extra={
				<div className="space-x-[10px]">
					<RouterLink
						to={seeUsageTo}
						active-class="display-none"
						style={{ color: antConfig.token?.['purple-6'] }}
						className="text-[13px] underline hover:no-underline"
					>
						See usage history
					</RouterLink>
					<AppButton className="border-[#D9D9D9] text-purple-6" onClick={onUpgrade}>
						Upgrade
					</AppButton>
				</div>
			}
		>
			<div className="space-y-[22px]">
				{showPendingDowngrade && <DowngradeIndication productType={productType} />}
				<div className="text-[13px]">
					<div className="grid grid-cols-2">
						<div>
							<strong className="block">Plan</strong>
							<span style={{ color: antConfig.token?.colorTextSecondary }}>{planReadableName}</span>
							{shouldShowTrial && (
								<div style={{ color: antConfig.token?.['purple-6'] }}>
									You are currently on a trial plan until <strong>{endOfTrial}</strong>
								</div>
							)}
						</div>
						<div>
							<div className="flex justify-between leading-[22px]">
								<strong className="block">Current quota cycle</strong>
								<span style={{ color: antConfig.token?.colorTextTertiary }}>
									Quota cycle: {quotaStartDate} - {quotaEndDate}
								</span>
							</div>
							<span className="flex justify-between leading-[22px]" style={{ color: antConfig.token?.colorTextSecondary }}>
								<span>
									{addCommasToNumber(currentUsedTasks)}/{isUnlimitedTasks ? 'Unlimited' : addCommasToNumber(totalTasks)} {noun}
								</span>
								{!isUnlimitedTasks && <span>{usagePercentage}%</span>}
							</span>
							{!isUnlimitedTasks && (
								<AppProgress
									showInfo={false}
									percent={usagePercentage}
									status={usagePercentage >= 100 ? 'exception' : undefined}
									strokeColor={usagePercentage >= 100 ? antConfig.token?.['red-6'] : antConfig.token?.['purple-6']}
								/>
							)}
							{isOveragesEnabled && !!overageAmount && (
								<div className="text-[13px]">
									<div className="flex justify-between">
										<div>
											Current overage of <strong>{addCommasToNumber(overageAmount)}</strong> {noun}
										</div>
										{!!estimatedOveragesCost && <div> Estimated cost: ${estimatedOveragesCost}</div>}
									</div>
									<div style={{ color: antConfig.token?.colorTextTertiary }}>
										Upgrade your plan before {quotaEndDate} to avoid overage charges
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</SectionCard>
	);
};

export default FeaturesCard;
